import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useAuthStore } from '@/stores/authStore'
import {
  getRecentTasks,
  getTaskActivity,
  getTaskDetail,
  type EntityHealthSummary,
  type TaskActivityParams,
  type TaskDetail,
  type TaskSummaryDto,
} from '@/api/lytAccountingClient'
import type { ApiError } from '@/api/clientUtils'

const ACTIVE_INTERVAL_MS = 10_000
const IDLE_INTERVAL_MS = 30_000
const HIDDEN_INTERVAL_MS = 60_000
const MAX_BACKOFF_MS = 30_000
const TERMINAL_STATUSES = ['completed', 'completed_with_errors', 'failed', 'cancelled', 'dead_lettered']

type ActivitySubscription = Required<Pick<TaskActivityParams, 'includeSummary' | 'includeRecent' | 'includeActive'>> & {
  recentLimit: number
  entityTypes: string[]
}

type RecentSubscription = {
  limit?: number
  entityType?: string
  status?: string
}

type InFlight<T> = {
  promise: Promise<T>
  controller: AbortController
}

function stableKey(value: unknown) {
  return JSON.stringify(value)
}

function normalizeActivityOptions(options: TaskActivityParams = {}): ActivitySubscription {
  return {
    includeSummary: options.includeSummary ?? false,
    includeRecent: options.includeRecent ?? false,
    includeActive: options.includeActive ?? false,
    recentLimit: options.recentLimit ?? 50,
    entityTypes: (options.entityTypes ?? []).map(type => type.toUpperCase()).sort(),
  }
}

function isTerminal(status?: string) {
  return Boolean(status && TERMINAL_STATUSES.includes(status))
}

export const useTaskActivityStore = defineStore('taskActivity', () => {
  const auth = useAuthStore()

  const summaries = ref<EntityHealthSummary[]>([])
  const recentTasks = ref<TaskSummaryDto[]>([])
  const activeTasksByEntityType = ref<Record<string, TaskDetail[]>>({})
  const detailCache = ref<Record<string, TaskDetail>>({})
  const recentListTasks = ref<TaskSummaryDto[]>([])
  const loading = ref(false)
  const recentListLoading = ref(false)
  const error = ref('')
  const lastRefreshAt = ref<Date | null>(null)
  const rateLimitedUntil = ref<Date | null>(null)
  const activitySubscriptionCount = ref(0)
  const recentSubscriptionCount = ref(0)

  const activitySubscriptions = new Map<number, ActivitySubscription>()
  const recentSubscriptions = new Map<number, RecentSubscription>()
  const inFlight = new Map<string, InFlight<unknown>>()
  let nextSubscriptionId = 1
  let pollTimer: ReturnType<typeof setTimeout> | null = null
  let failureCount = 0
  let visibilityListenerInstalled = false

  const subscriberCount = computed(() => activitySubscriptionCount.value + recentSubscriptionCount.value)
  const isRateLimited = computed(() => Boolean(rateLimitedUntil.value && rateLimitedUntil.value.getTime() > Date.now()))
  const hasActiveTasks = computed(() =>
    Object.values(activeTasksByEntityType.value).some(tasks => tasks.some(task => !isTerminal(task.status)))
  )

  function activeTasksFor(entityType: string) {
    return activeTasksByEntityType.value[entityType.toUpperCase()] ?? []
  }

  function recentTasksFor(entityTypes: string[], terminalOnly = false) {
    const allowed = new Set(entityTypes.map(type => type.toUpperCase()))
    return recentTasks.value.filter(task => {
      if (!allowed.has(task.entityType)) return false
      return terminalOnly ? isTerminal(task.status) : true
    })
  }

  function subscribeActivity(options: TaskActivityParams = {}) {
    const id = nextSubscriptionId++
    activitySubscriptions.set(id, normalizeActivityOptions(options))
    activitySubscriptionCount.value = activitySubscriptions.size
    installVisibilityListener()
    void refreshActivity()
    scheduleNextPoll()
    return () => {
      activitySubscriptions.delete(id)
      activitySubscriptionCount.value = activitySubscriptions.size
      if (!subscriberCount.value) {
        stopPolling()
      } else {
        scheduleNextPoll()
      }
    }
  }

  function subscribeRecent(params: RecentSubscription = {}) {
    const id = nextSubscriptionId++
    recentSubscriptions.set(id, { ...params })
    recentSubscriptionCount.value = recentSubscriptions.size
    installVisibilityListener()
    void refreshRecent(params, true)
    scheduleNextPoll()
    return () => {
      recentSubscriptions.delete(id)
      recentSubscriptionCount.value = recentSubscriptions.size
      if (!subscriberCount.value) {
        stopPolling()
      } else {
        scheduleNextPoll()
      }
    }
  }

  async function refreshActivity(force = false) {
    if (activitySubscriptions.size === 0) return
    if (!force && isRateLimited.value) return

    const params = effectiveActivityParams()
    const key = `activity:${stableKey(params)}`
    abortMatchingRequests('activity:', key)
    loading.value = true
    error.value = ''

    try {
      const response = await singleFlight(key, signal => getTaskActivity(params, { signal }))
      if (response.summary) summaries.value = response.summary.entitySummaries ?? []
      if (response.recent) recentTasks.value = response.recent.tasks ?? []
      if (response.active) activeTasksByEntityType.value = response.active.tasksByEntityType ?? {}
      lastRefreshAt.value = new Date()
      failureCount = 0
      rateLimitedUntil.value = null
      scheduleNextPoll()
    } catch (err) {
      handleRequestError(err)
    } finally {
      loading.value = false
    }
  }

  async function refreshRecent(params: RecentSubscription = {}, force = false) {
    if (!force && isRateLimited.value) return
    const key = `recent:${stableKey(params)}`
    recentListLoading.value = true

    try {
      const response = await singleFlight(key, signal => getRecentTasks(params, { signal }))
      recentListTasks.value = response.tasks ?? []
      lastRefreshAt.value = new Date()
      failureCount = 0
      rateLimitedUntil.value = null
    } catch (err) {
      handleRequestError(err)
    } finally {
      recentListLoading.value = false
    }
  }

  async function loadTaskDetail(taskId: number | string, force = false) {
    const key = String(taskId)
    if (!force && detailCache.value[key]) return detailCache.value[key]

    const detail = await singleFlight(`detail:${key}`, signal => getTaskDetail(taskId, { signal }))
    detailCache.value = { ...detailCache.value, [key]: detail }
    return detail
  }

  function refreshNow(options?: TaskActivityParams) {
    if (options) {
      const id = nextSubscriptionId++
      activitySubscriptions.set(id, normalizeActivityOptions(options))
      activitySubscriptionCount.value = activitySubscriptions.size
      return refreshActivity(true).finally(() => {
        activitySubscriptions.delete(id)
        activitySubscriptionCount.value = activitySubscriptions.size
      })
    }
    return Promise.all([
      refreshActivity(true),
      ...Array.from(recentSubscriptions.values()).map(params => refreshRecent(params, true)),
    ])
  }

  function reset() {
    abortAllRequests()
    summaries.value = []
    recentTasks.value = []
    activeTasksByEntityType.value = {}
    detailCache.value = {}
    recentListTasks.value = []
    error.value = ''
    lastRefreshAt.value = null
    rateLimitedUntil.value = null
    failureCount = 0
    scheduleNextPoll()
  }

  function effectiveActivityParams(): TaskActivityParams {
    const subscriptions = Array.from(activitySubscriptions.values())
    const entityTypes = new Set<string>()
    for (const subscription of subscriptions) {
      subscription.entityTypes.forEach(type => entityTypes.add(type))
    }

    return {
      includeSummary: subscriptions.some(subscription => subscription.includeSummary),
      includeRecent: subscriptions.some(subscription => subscription.includeRecent),
      includeActive: subscriptions.some(subscription => subscription.includeActive),
      recentLimit: Math.min(100, Math.max(...subscriptions.map(subscription => subscription.recentLimit), 50)),
      entityTypes: Array.from(entityTypes).sort(),
    }
  }

  async function singleFlight<T>(key: string, request: (signal: AbortSignal) => Promise<T>) {
    const existing = inFlight.get(key) as InFlight<T> | undefined
    if (existing) return existing.promise

    const controller = new AbortController()
    const promise = request(controller.signal).finally(() => {
      inFlight.delete(key)
    })
    inFlight.set(key, { promise, controller } as InFlight<unknown>)
    return promise
  }

  function handleRequestError(err: unknown) {
    const apiError = err as ApiError
    if (apiError.isAbort) return
    if (apiError.status === 429) {
      const retryAfterSeconds = apiError.retryAfterSeconds ?? 30
      rateLimitedUntil.value = new Date(Date.now() + retryAfterSeconds * 1000)
      error.value = apiError.message
      scheduleNextPoll()
      return
    }

    failureCount += 1
    error.value = (err as Error)?.message || 'Failed to load task activity'
  }

  function scheduleNextPoll() {
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
    if (!subscriberCount.value) return

    const delay = nextPollDelay()
    pollTimer = setTimeout(async () => {
      await refreshActivity()
      await Promise.all(Array.from(recentSubscriptions.values()).map(params => refreshRecent(params)))
      scheduleNextPoll()
    }, delay)
  }

  function nextPollDelay() {
    if (rateLimitedUntil.value && rateLimitedUntil.value.getTime() > Date.now()) {
      return Math.max(0, rateLimitedUntil.value.getTime() - Date.now())
    }
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return HIDDEN_INTERVAL_MS
    }
    if (failureCount > 0) {
      return Math.min(MAX_BACKOFF_MS, 1000 * 2 ** failureCount)
    }
    return hasActiveTasks.value ? ACTIVE_INTERVAL_MS : IDLE_INTERVAL_MS
  }

  function stopPolling() {
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
    abortMatchingRequests('activity:')
    abortMatchingRequests('recent:')
  }

  function abortMatchingRequests(prefix: string, exceptKey?: string) {
    for (const [key, request] of inFlight.entries()) {
      if (key.startsWith(prefix) && key !== exceptKey) {
        request.controller.abort()
        inFlight.delete(key)
      }
    }
  }

  function abortAllRequests() {
    for (const request of inFlight.values()) {
      request.controller.abort()
    }
    inFlight.clear()
  }

  function installVisibilityListener() {
    if (visibilityListenerInstalled || typeof document === 'undefined') return
    document.addEventListener('visibilitychange', handleVisibilityChange)
    visibilityListenerInstalled = true
  }

  function handleVisibilityChange() {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      void refreshNow()
    }
    scheduleNextPoll()
  }

  watch(() => auth.currentApiKey, () => reset())

  return {
    summaries,
    recentTasks,
    activeTasksByEntityType,
    detailCache,
    recentListTasks,
    loading,
    recentListLoading,
    error,
    lastRefreshAt,
    rateLimitedUntil,
    subscriberCount,
    isRateLimited,
    hasActiveTasks,
    activeTasksFor,
    recentTasksFor,
    subscribeActivity,
    subscribeRecent,
    refreshActivity,
    refreshRecent,
    refreshNow,
    loadTaskDetail,
    reset,
  }
})
